// stores/auth.ts — сессия пользователя: access/refresh в localStorage, профиль, tenant slug.
// tryRefresh — ОДИН in-flight refresh на все параллельные 401 (shared-promise), без рекурсии.
// session_expired → редирект на /login + очистка query-кэша (иначе шторм 401→тостов).
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api } from '@/api/client';
import { queryClient } from '@/lib/queryClient';
import { settings } from '@/lib/settings';

interface LoginResponse {
  accessToken: string; refreshToken: string;
  user: { id: string; email: string; role: 'owner' | 'admin' | 'editor' | 'ai' | 'superadmin' | 'platform'; tenantId: number | null };
  // платформный админ с включённой 2FA: токенов нет, дальше — /admin (свой 2FA-флоу)
  twoFactorRequired?: boolean; twoFactorToken?: string;
}

const LS_ACCESS = 'mcps.access';
const LS_REFRESH = 'mcps.refresh';
const LS_USER = 'mcps.user';
const LS_TENANT_SLUG = 'mcps.tenant';
const LS_PROJECT = 'mcps.project';

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem(LS_ACCESS));
  const refreshToken = ref<string | null>(localStorage.getItem(LS_REFRESH));
  const user = ref<LoginResponse['user'] | null>(
    (() => { try { return JSON.parse(localStorage.getItem(LS_USER) ?? 'null'); } catch { return null; } })(),
  );
  const tenantSlug = ref<string | null>(localStorage.getItem(LS_TENANT_SLUG) ?? settings.defaultTenantSlug);
  /** Активный проект (site_state.slug) — шлётся заголовком x-project на каждый запрос. */
  const projectSlug = ref<string | null>(localStorage.getItem(LS_PROJECT));

  const isAuthed = computed(() => Boolean(accessToken.value && user.value));
  const role = computed(() => user.value?.role ?? null);

  function persist(): void {
    if (accessToken.value) localStorage.setItem(LS_ACCESS, accessToken.value); else localStorage.removeItem(LS_ACCESS);
    if (refreshToken.value) localStorage.setItem(LS_REFRESH, refreshToken.value); else localStorage.removeItem(LS_REFRESH);
    if (user.value) localStorage.setItem(LS_USER, JSON.stringify(user.value)); else localStorage.removeItem(LS_USER);
    if (tenantSlug.value) localStorage.setItem(LS_TENANT_SLUG, tenantSlug.value);
    if (projectSlug.value) localStorage.setItem(LS_PROJECT, projectSlug.value); else localStorage.removeItem(LS_PROJECT);
  }

  /** Сменить активный проект (роут /p/:slug, ProjectsView, TopBar-переключатель). */
  function setProject(slug: string | null): void {
    if (projectSlug.value === slug) return;
    projectSlug.value = slug;
    persist();
    queryClient.clear(); // кэш старого проекта недействителен
  }

  /** Обновить профиль (после change-email: email в UI/заголовках должен обновиться). */
  function setUser(u: LoginResponse['user']): void {
    user.value = u;
    persist();
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await api.post<LoginResponse>('/api/auth/login', { email, password }, { noAuth: true });
    if (res.twoFactorRequired) {
      // Платформный админ (2FA): сессии здесь нет — кабинет платформы на /admin
      // завершит вход кодом TOTP. Локальный storage не трогаем.
      window.location.href = '/admin/login';
      return;
    }
    accessToken.value = res.accessToken;
    refreshToken.value = res.refreshToken;
    user.value = res.user;
    if (res.user.tenantId) {
      // slug тенанта узнаём из профиля/заголовка — на демо жёстко 'demo'
    }
    persist();
  }

  // Само-регистрация (P0-2) намеренно без store: API не выдаёт токены до
  // подтверждения email (routes/auth.ts) — флоу RegisterView → письмо → /confirm → login.

  /** Обмен refresh → access. Дедуплицирует параллельные вызовы (shared-promise):
   *  на маунте летят несколько 401 — refresh уходит один раз, иначе второй уйдёт с
   *  уже инвалидированным (ротация) refresh-токеном → logout. */
  let refreshInFlight: Promise<boolean> | null = null;
  async function tryRefresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      if (!refreshToken.value) return false;
      try {
        const res = await api.post<LoginResponse>('/api/auth/refresh', { refreshToken: refreshToken.value }, { noAuth: true });
        accessToken.value = res.accessToken;
        if (res.refreshToken) refreshToken.value = res.refreshToken;
        user.value = res.user;
        persist();
        return true;
      } catch {
        return false;
      }
    })().finally(() => { refreshInFlight = null; });
    return refreshInFlight;
  }

  function logout(reason?: 'session_expired' | 'manual'): void {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    persist();
    // сбрасываем кэш запросов — иначе Vue Query продолжит слать 401 в цикле
    queryClient.clear();
    // lazy-import router чтобы не плодить цикл модулей (router.ts → useAuthStore → router.ts)
    const goToLogin = (): void => { void import('@/router').then(({ router }) => router.push({ name: 'login' })); };
    if (reason === 'session_expired') {
      import('@/stores/toast').then(({ useToast }) => useToast().error('Сессия истекла — войдите снова'));
      goToLogin();
    } else if (reason === 'manual') {
      goToLogin();
    }
  }

  return { accessToken, refreshToken, user, tenantSlug, projectSlug, isAuthed, role, login, tryRefresh, logout, persist, setProject, setUser };
});
